import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Card,
    CardContent,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Grid,
    Box,
    TextField,
    Alert,
    Divider,
    Container,
    Stack,
    InputAdornment
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Notyf } from 'notyf';
import 'notyf/notyf.min.css';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, Mail, User, Phone, Check, ArrowRight } from 'lucide-react'; // Added ArrowRight

// Initialize Notyf for notifications
const notyf = new Notyf({
    position: { x: 'center', y: 'top' },
    duration: 4000,
});

// --- Constants (Updated Pricing) ---
const plans = {
    free: {
        monthly: 0,
        yearly: 0,
        features: [
            'Create Invoice',
            'Invoices Report',
            'Add Customers',
            'Add Products',
        ]
    },
    basic: {
        monthly: 1000.00,
        yearly: 10800.00, // 1000 * 12 * 0.9 (10% discount applied)
        features: [
            'Create Invoice',
            'Add Expenses',
            'Manage Inventory',
            'And More',
        ]
    },
    premium: {
        monthly: 2500.00,
        yearly: 27000.00, // 2500 * 12 * 0.9 (10% discount applied)
        features: ['All Basic Features', 'Manage Inventory', 'Return Management', '24/7 support']
    }
};

const PAYHERE_MERCHANT_ID = "243166";
const PAYHERE_LIVE_URL = "https://www.payhere.lk/pay/checkout";
const PAYHERE_NOTIFY_URL = "https://admin.bizstor.lk/public/api/payment-notification"; // Your backend notification URL
const API_BASE_URL = "https://admin.bizstor.lk/public/api"; // Your API base URL

// --- Main Component ---
export default function SinglePageCheckout() {
    // --- State Management ---
    const [selectedPlan, setSelectedPlan] = useState('free');
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [paymentData, setPaymentData] = useState({
        merchant_id: PAYHERE_MERCHANT_ID || '',
        subscription_id: `SUB_${Math.random().toString(36).substr(2, 9)}`,
        amount: 0,
        currency: 'LKR',
        subscription_period: 'monthly',
        user_email: '',
        user_phone: '',
        first_name: '',
        last_name: '',
    });
    const [errors, setErrors] = useState({});
    const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error', 'cancelled'
    const [isCompleted, setIsCompleted] = useState(false); // State to show thank you page

    // Email verification states
    const [isEmailVerificationSent, setIsEmailVerificationSent] = useState(false);
    const [enteredCode, setEnteredCode] = useState('');
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [verifyingEmail, setVerifyingEmail] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);

    // Payment processing states
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [paymentRef, setPaymentRef] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Effect to set up PayHere event handlers
    useEffect(() => {
        const setupPayHereEventHandlers = () => {
            window.payhere = window.payhere || {};

            window.payhere.onCompleted = function onCompleted(orderId) {
                //console.log('Payment completed. Order ID:', orderId);
                saveSubscriptionToDatabase(orderId, paymentData); // Use the current state
            };

            window.payhere.onDismissed = function onDismissed() {
                setPaymentProcessing(false);
                setSubmissionStatus('cancelled');
                notyf.error('Payment was cancelled.');
            };

            window.payhere.onError = function onError(error) {
                console.error("PayHere Error:", error);
                setPaymentProcessing(false);
                setSubmissionStatus('error');
                notyf.error('Payment failed. Please try again.');
            };
        };

        if (!document.getElementById('payhere-script')) {
            const script = document.createElement('script');
            script.id = 'payhere-script';
            script.src = 'https://www.payhere.lk/lib/payhere.js';
            script.async = true;
            script.onload = () => {
                setupPayHereEventHandlers();
            };
            script.onerror = (error) => {
                console.error('Failed to load PayHere script:', error);
                notyf.error('Payment gateway script failed to load. Please refresh the page.');
            };
            document.body.appendChild(script);
        } else {
            setupPayHereEventHandlers();
        }
    }, [paymentData]); // Dependency on paymentData ensures it has the latest user info

    // Effect to update payment amount when plan or billing cycle changes
    useEffect(() => {
        const amount = billingCycle === 'yearly'
            ? plans[selectedPlan].yearly
            : plans[selectedPlan].monthly;

        setPaymentData(prev => ({
            ...prev,
            amount,
            subscription_period: billingCycle,
        }));
    }, [selectedPlan, billingCycle]);


    // --- Core Functions ---

    const saveSubscriptionToDatabase = async (orderId, currentPaymentData) => {
        try {
            const response = await fetch('https://admin.bizstor.lk/public/api/save-subscription', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    email: currentPaymentData.user_email,
                    plan: selectedPlan,
                    billing_cycle: billingCycle,
                    subscription_id: currentPaymentData.subscription_id,
                    amount: currentPaymentData.amount,
                    order_id: orderId,
                    payment_status: 'completed',
                    user_phone: currentPaymentData.user_phone,
                    first_name: currentPaymentData.first_name,
                    last_name: currentPaymentData.last_name
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to save subscription data');
            }

            notyf.success('Subscription activated successfully!');
            setIsCompleted(true);

        } catch (error) {
            console.error('Failed to save subscription:', error);
            notyf.error('There was an issue saving your subscription. Please contact support with your order ID: ' + orderId);
        } finally {
            setPaymentProcessing(false);
        }
    };


    const handlePaymentConfirmation = async () => {
        try {
            setPaymentProcessing(true);

            const orderResponse = await fetch(`${API_BASE_URL}/generate-order-id`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    subscription_id: paymentData.subscription_id,
                    plan: selectedPlan,
                    amount: paymentData.amount
                }),
            });

            const orderData = await orderResponse.json();
            if (!orderResponse.ok) {
                throw new Error(orderData.error || 'Failed to generate order ID');
            }

            const orderId = orderData.order_id;
            const payment = {
                sandbox: true,
                merchant_id: PAYHERE_MERCHANT_ID,
                return_url: window.location.href,
                cancel_url: window.location.href,
                notify_url: PAYHERE_NOTIFY_URL,
                order_id: orderId,
                items: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan (${billingCycle})`,
                amount: paymentData.amount.toString(),
                currency: paymentData.currency,
                hash: orderData.hash,
                first_name: paymentData.first_name,
                last_name: paymentData.last_name,
                email: paymentData.user_email,
                phone: paymentData.user_phone,
                address: "N/A", city: "Colombo", country: "Sri Lanka",
            };

            setPaymentRef(orderId);

            if (window.payhere && typeof window.payhere.startPayment === 'function') {
                window.payhere.startPayment(payment);
            } else {
                throw new Error('PayHere is not properly loaded');
            }

        } catch (error) {
            console.error('Payment initiation failed:', error);
            setSubmissionStatus('error');
            setPaymentProcessing(false);
            notyf.error('Payment initiation failed: ' + error.message);
        }
    };

    const handleFreePlanActivation = async () => {
        setPaymentProcessing(true);
        const fakeOrderId = `FREE-${Date.now()}`;
        await saveSubscriptionToDatabase(fakeOrderId, paymentData);
    };

    // --- UI Handlers ---

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));

        if (name === 'user_email') {
            setIsEmailVerificationSent(false);
            setIsEmailVerified(false);
            setEnteredCode('');
        }
    };

    const handlePlanChange = (event) => setSelectedPlan(event.target.value);
    const handleBillingChange = (event) => setBillingCycle(event.target.value);
    const handleCodeChange = (e) => setEnteredCode(e.target.value);

    const handleSendVerificationCode = async () => {
        if (!paymentData.user_email) {
            setErrors(prev => ({ ...prev, user_email: 'Email is required to send a code.' }));
            return;
        }
        setVerifyingEmail(true);
        try {
            const response = await fetch(`${API_BASE_URL}/send-verification-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: paymentData.user_email }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to send code.');
            setIsEmailVerificationSent(true);
            notyf.success(`Verification code sent to ${paymentData.user_email}`);
        } catch (error) {
            notyf.error(error.message);
        } finally {
            setVerifyingEmail(false);
        }
    };

    const handleVerifyCode = async () => {
        setVerifyingCode(true);
        try {
            const response = await fetch(`${API_BASE_URL}/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: paymentData.user_email, code: enteredCode }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Invalid code.');
            setIsEmailVerified(true);
            notyf.success('Email verified successfully!');
            setErrors(prev => ({ ...prev, verification: null }));
        } catch (error) {
            setErrors(prev => ({ ...prev, verification: error.message }));
        } finally {
            setVerifyingCode(false);
        }
    };

    const handleSubmit = () => {
        const newErrors = {};
        if (!paymentData.first_name) newErrors.first_name = 'First name is required';
        if (!paymentData.last_name) newErrors.last_name = 'Last name is required';
        if (!paymentData.user_email) newErrors.user_email = 'Email is required';
        if (!paymentData.user_phone) newErrors.user_phone = 'Phone is required';
        if (!isEmailVerified) {
            newErrors.verification = 'Please verify your email address.';
            notyf.error('Email verification is required to proceed.');
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setErrors({});

        if (selectedPlan === 'free') {
            handleFreePlanActivation();
        } else {
            handlePaymentConfirmation();
        }
    };

    // --- Render Logic ---

    if (isCompleted) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', textAlign: 'center', p: 3, bgcolor: '#F8FAFC' }}>
                <Box sx={{ width: 80, height: 80, bgcolor: '#ECFDF5', color: '#10B981', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <CheckCircle2 size={48} />
                </Box>
                <Typography variant="h3" fontWeight={'800'} color="#0F172A" gutterBottom>
                    Payment Successful!
                </Typography>
                <Typography variant="h6" color="#64748B" mb={4}>
                    Your {selectedPlan} plan ({billingCycle}) has been successfully activated.
                </Typography>
                <Button 
                    variant="contained" 
                    size='large' 
                    sx={{ borderRadius: '12px', px: 6, py: 1.5, bgcolor: '#4F46E5', textTransform: 'none', fontWeight: 600, fontSize: '1rem', '&:hover': { bgcolor: '#4338CA' } }} 
                    onClick={() => navigate('/login')}
                >
                    Go to Dashboard
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', py: { xs: 4, md: 8 }  }}>
            <Container maxWidth="xl" sx={{px: { xs: 3, md: 6 },}}>
                <Box textAlign="center" mb={6}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>
                        Complete Your Subscription
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748B', fontSize: '1.1rem' }}>
                        Choose your plan, fill in your details, and get started today.
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 4, md: 5 }}>
                    {/* --- Left Column: Plans & Details --- */}
                    <Grid item xs={12} md={7}>
                        
                        {/* 1. Plan Selection */}
                        <Card elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #E2E8F0', mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#EFF6FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>1</Box>
                                <Typography variant="h6" fontWeight="700" color="#0F172A">Choose Your Plan</Typography>
                            </Box>
                            
                            {/* Modern Billing Toggle */}
                            <Box display="flex" mb={4}>
                                <Box sx={{ display: 'inline-flex', bgcolor: '#F1F5F9', borderRadius: '999px', p: 0.5, border: '1px solid #E2E8F0' }}>
                                    <Button
                                        onClick={() => handleBillingChange({ target: { value: 'monthly' } })}
                                        disableElevation
                                        sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, bgcolor: billingCycle === 'monthly' ? '#ffffff' : 'transparent', color: billingCycle === 'monthly' ? '#0F172A' : '#64748B', borderRadius: '999px', boxShadow: billingCycle === 'monthly' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none' }}
                                    >
                                        Monthly
                                    </Button>
                                    <Button
                                        onClick={() => handleBillingChange({ target: { value: 'yearly' } })}
                                        disableElevation
                                        sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, bgcolor: billingCycle === 'yearly' ? '#ffffff' : 'transparent', color: billingCycle === 'yearly' ? '#0F172A' : '#64748B', borderRadius: '999px', boxShadow: billingCycle === 'yearly' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none' }}
                                    >
                                        Yearly (Save 10%)
                                    </Button>
                                </Box>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}><PlanCard name="Free" value="free" selected={selectedPlan === 'free'} onChange={handlePlanChange} billingCycle={billingCycle} /></Grid>
                                <Grid item xs={12} sm={4}><PlanCard name="Basic" value="basic" selected={selectedPlan === 'basic'} onChange={handlePlanChange} billingCycle={billingCycle} /></Grid>
                                <Grid item xs={12} sm={4}><PlanCard name="Premium" value="premium" selected={selectedPlan === 'premium'} onChange={handlePlanChange} billingCycle={billingCycle} /></Grid>
                            </Grid>

                            {/* Added "See full plan details" Button Here */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Button 
                                    variant="text" 
                                    endIcon={<ArrowRight size={16} />}
                                    onClick={() => navigate('/plan-and-pricing')} 
                                    sx={{ 
                                        textTransform: 'none', 
                                        fontWeight: 600, 
                                        color: '#4F46E5',
                                        fontSize: '0.95rem',
                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline', color: '#4338CA' }
                                    }}
                                >
                                    See full plan details
                                </Button>
                            </Box>
                        </Card>

                        {/* 2. Personal Information */}
                        <Card elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #E2E8F0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#EFF6FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>2</Box>
                                <Typography variant="h6" fontWeight="700" color="#0F172A">Personal Information</Typography>
                            </Box>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="First Name" name="first_name" value={paymentData.first_name} onChange={handleInputChange} error={!!errors.first_name} helperText={errors.first_name} InputProps={{ startAdornment: <InputAdornment position="start"><User size={18} color="#94A3B8" /></InputAdornment> }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Last Name" name="last_name" value={paymentData.last_name} onChange={handleInputChange} error={!!errors.last_name} helperText={errors.last_name} InputProps={{ startAdornment: <InputAdornment position="start"><User size={18} color="#94A3B8" /></InputAdornment> }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Phone Number" name="user_phone" value={paymentData.user_phone} onChange={handleInputChange} error={!!errors.user_phone} helperText={errors.user_phone} InputProps={{ startAdornment: <InputAdornment position="start"><Phone size={18} color="#94A3B8" /></InputAdornment> }} />
                                </Grid>
                                
                                {/* Email Verification Flow */}
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth 
                                        label="Email Address" 
                                        name="user_email" 
                                        type="email" 
                                        value={paymentData.user_email} 
                                        onChange={handleInputChange} 
                                        error={!!errors.user_email} 
                                        helperText={errors.user_email} 
                                        disabled={isEmailVerified || isEmailVerificationSent}
                                        InputProps={{ 
                                            startAdornment: <InputAdornment position="start"><Mail size={18} color="#94A3B8" /></InputAdornment>,
                                            endAdornment: isEmailVerified && <InputAdornment position="end"><CheckCircle2 size={20} color="#10B981" /></InputAdornment>
                                        }} 
                                    />
                                </Grid>

                                {!isEmailVerified ? (
                                    <Grid item xs={12}>
                                        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                                            <Button 
                                                variant="outlined" 
                                                onClick={handleSendVerificationCode} 
                                                disabled={!paymentData.user_email || verifyingEmail || isEmailVerificationSent} 
                                                sx={{ flexShrink: 0, height: '56px', borderRadius: '12px', borderColor: '#E2E8F0', color: '#475569', '&:hover': { borderColor: '#4F46E5', color: '#4F46E5', bgcolor: '#EFF6FF' } }}
                                            >
                                                {verifyingEmail ? <CircularProgress size={24} /> : isEmailVerificationSent ? 'Code Sent' : 'Send Code'}
                                            </Button>
                                            
                                            {isEmailVerificationSent && (
                                                <Box display="flex" gap={2} width="100%">
                                                    <TextField label="Enter Code" value={enteredCode} onChange={handleCodeChange} error={!!errors.verification} helperText={errors.verification} fullWidth />
                                                    <Button variant="contained" onClick={handleVerifyCode} disabled={!enteredCode || verifyingCode} sx={{ flexShrink: 0, height: '56px', borderRadius: '12px', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>
                                                        {verifyingCode ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Card>
                    </Grid>

                    {/* --- Right Column: Summary --- */}
                    <Grid item xs={12} md={5}>
                        <Box sx={{ position: 'sticky', top: 24 }}>
                            <Card elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #E2E8F0', bgcolor: '#ffffff', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)' }}>
                                <Typography variant="h6" fontWeight="800" color="#0F172A" mb={3}>Order Summary</Typography>
                                
                                <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: '16px', mb: 3 }}>
                                    <Box display="flex" justifyContent="space-between" mb={2}>
                                        <Typography color="#64748B" fontWeight="500">Plan</Typography>
                                        <Typography fontWeight="700" color="#0F172A">{selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography color="#64748B" fontWeight="500">Billing Cycle</Typography>
                                        <Typography fontWeight="700" color="#0F172A">{billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)}</Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 3, borderColor: '#F1F5F9' }} />
                                
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                                    <Typography variant="h6" fontWeight="700" color="#0F172A">Total Due</Typography>
                                    <Box textAlign="right">
                                        <Typography variant="h4" fontWeight="800" color="#4F46E5">
                                            LKR {paymentData.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </Typography>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="600" textTransform="uppercase" letterSpacing="0.05em">
                                            {billingCycle === 'yearly' ? 'Billed Annually' : 'Billed Monthly'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={handleSubmit}
                                    disabled={paymentProcessing}
                                    startIcon={!paymentProcessing && <ShieldCheck size={20} />}
                                    sx={{ 
                                        py: 2, 
                                        borderRadius: '12px', 
                                        fontWeight: 700, 
                                        fontSize: '1.05rem', 
                                        textTransform: 'none',
                                        bgcolor: '#4F46E5', 
                                        boxShadow: '0 10px 20px -5px rgba(79, 70, 229, 0.4)',
                                        '&:hover': { bgcolor: '#4338CA', boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.5)' } 
                                    }}
                                >
                                    {paymentProcessing ? <CircularProgress size={26} color="inherit" /> : (selectedPlan === 'free' ? 'Activate Free Plan' : 'Pay Securely Now')}
                                </Button>
                                
                                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#94A3B8', mt: 3, lineHeight: 1.5 }}>
                                    Payments are processed securely via PayHere. <br/> This is a one-time payment. Manual renewal required.
                                </Typography>
                            </Card>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

// --- Reusable PlanCard Component ---
function PlanCard({ name, value, selected, onChange, billingCycle }) {
    const plan = plans[value.toLowerCase()];
    const price = billingCycle === 'yearly' ? plan.yearly : plan.monthly;

    return (
        <Card
            onClick={() => onChange({ target: { value } })}
            elevation={0}
            sx={{
                borderColor: selected ? '#4F46E5' : '#E2E8F0',
                borderWidth: selected ? 2 : 1,
                borderStyle: 'solid',
                bgcolor: selected ? '#EEF2FF' : '#ffffff',
                height: '100%',
                cursor: 'pointer',
                borderRadius: '16px',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: '#4F46E5',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
                }
            }}
        >
            {/* Custom Modern Checkbox */}
            <Box 
                sx={{ 
                    position: 'absolute', 
                    top: 12, 
                    right: 12, 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    border: selected ? 'none' : '2px solid #CBD5E1',
                    bgcolor: selected ? '#4F46E5' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                }}
            >
                {selected && <Check size={14} strokeWidth={3} />}
            </Box>

            <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" fontWeight="700" color="#64748B" textTransform="uppercase" letterSpacing="0.05em" mb={1}>{name}</Typography>
                <Typography variant="h6" fontWeight="700" color="#0F172A" mb={0.5}>
                    LKR {price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography color="#94A3B8" variant="caption" fontWeight="600">
                    / {billingCycle === 'yearly' ? 'year' : 'month'}
                </Typography>
                
                <Divider sx={{ my: 2, borderColor: selected ? '#C7D2FE' : '#F1F5F9' }} />
                
                <Stack spacing={1}>
                    {plan.features.map((feature, index) => (
                        <Stack direction="row" spacing={1} key={index} alignItems="center">
                            <CheckCircle2 size={16} color={selected ? '#4F46E5' : '#94A3B8'} />
                            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
                                {feature}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
}